const normalizeBooleanOption = (value, defaultValue = true) => {
  if (value === undefined || value === null) {
    return defaultValue;
  }

  if (typeof value === 'string') {
    return !['0', 'false', 'no'].includes(value.trim().toLowerCase());
  }

  return Boolean(value);
};

const normalizePriceValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numericValue = Number(String(value).replace(',', '.'));
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatPriceValue = (value) => {
  const normalizedValue = normalizePriceValue(value);

  if (normalizedValue === null) {
    return '';
  }

  return Number.isInteger(normalizedValue)
    ? String(normalizedValue)
    : normalizedValue.toFixed(2).replace(/\.0+$/, '').replace(/\.?0+$/, '');
};

export const isSprayRollOnOptionEnabled = (product) => normalizeBooleanOption(product?.has_spray_roll_on_option, true);

export const parseSprayRollOnOptions = (product) => {
  const raw = product?.spray_roll_on_options;
  if (!raw) return { spray: true, roll_on: true };
  if (typeof raw === 'object' && !Array.isArray(raw)) return { spray: raw.spray !== false, roll_on: raw.roll_on !== false };
  try {
    const parsed = JSON.parse(raw);
    return { spray: parsed.spray !== false, roll_on: parsed.roll_on !== false };
  } catch {
    return { spray: true, roll_on: true };
  }
};

export const isSprayAvailable = (product) => parseSprayRollOnOptions(product).spray;
export const isRollOnAvailable = (product) => parseSprayRollOnOptions(product).roll_on;

export const parseBottleSizes = (product) => {
  const raw = product?.bottle_sizes;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(s => s && s.size);
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(s => s && s.size) : [];
  } catch {
    return [];
  }
};

export const isBottleSizeOptionEnabled = (product) => {
  const sizes = parseBottleSizes(product);
  if (sizes.length > 0) return true;
  return normalizeBooleanOption(product?.has_bottle_size_option, false);
};

export const getDefaultBottleSize = (product) => {
  if (product) {
    const sizes = parseBottleSizes(product);
    if (sizes.length > 0) return sizes[0].size;
  }
  return '100ml';
};

export const hasBottleSizePriceRange = (product) => {
  const sizes = parseBottleSizes(product);
  if (sizes.length >= 2) return true;
  return normalizeBooleanOption(product?.has_bottle_size_option, false)
    && normalizePriceValue(product?.price) !== null
    && normalizePriceValue(product?.new_price) !== null;
};

export const getProductUnitPrice = (product, size) => {
  const sizes = parseBottleSizes(product);
  if (sizes.length > 0) {
    const normalizedSize = String(size || '').trim();
    const found = sizes.find(s => s.size === normalizedSize);
    if (found) return normalizePriceValue(found.price);
    return normalizePriceValue(sizes[0]?.price);
  }

  const basePrice = normalizePriceValue(product?.price);
  const alternatePrice = normalizePriceValue(product?.new_price);
  const isSizeEnabled = normalizeBooleanOption(product?.has_bottle_size_option, false);
  const normalizedSize = String(size || '').trim();

  if (isSizeEnabled && alternatePrice !== null) {
    if (normalizedSize === '250ml') {
      return alternatePrice;
    }

    if (normalizedSize === '100ml' || normalizedSize === '') {
      return basePrice ?? alternatePrice;
    }
  }

  return alternatePrice ?? basePrice;
};

export const getProductPriceLabel = (product, size = null) => {
  const sizes = parseBottleSizes(product);
  if (sizes.length >= 2 && !size) {
    const prices = sizes.map(s => normalizePriceValue(s.price)).filter(p => p !== null);
    if (prices.length >= 2) {
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      return `${formatPriceValue(min)}-${formatPriceValue(max)}€`;
    }
  }

  if (sizes.length > 0) {
    const unitPrice = getProductUnitPrice(product, size);
    return unitPrice === null ? '' : `${formatPriceValue(unitPrice)}€`;
  }

  if (hasBottleSizePriceRange(product)) {
    if (!size) {
      return `${formatPriceValue(product?.price)}-${formatPriceValue(product?.new_price)}€`;
    }

    return `${formatPriceValue(getProductUnitPrice(product, size))}€`;
  }

  const unitPrice = getProductUnitPrice(product, size);
  return unitPrice === null ? '' : `${formatPriceValue(unitPrice)}€`;
};

export { formatPriceValue, normalizePriceValue };
